import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Mail, Book, FileQuestion, ArrowRight, Home, ArrowLeft, Video, Briefcase } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';

const faqs = [
  {
    question: 'How does VoiceThera work?',
    answer: 'VoiceThera uses advanced AI to create personalized meditation sessions tailored to your unique needs, preferences, and goals. Simply answer a few questions about what you need, and we\'ll generate a custom guided meditation session with soothing voice guidance and optional background music.',
  },
  {
    question: 'What makes VoiceThera different from other meditation apps?',
    answer: 'Unlike traditional meditation apps with pre-recorded sessions, VoiceThera generates each session specifically for you. Every meditation is unique and personalized based on your current emotional state, goals, and preferences. We also offer specialized categories and group session capabilities.',
  },
  {
    question: 'How long does it take to generate a session?',
    answer: 'Most sessions are generated within 30-60 seconds. You can choose session lengths from 5 to 60 minutes, and we\'ll create the perfect meditation experience for your available time.',
  },
  {
    question: 'Can I use VoiceThera without creating an account?',
    answer: 'You need to create an account to use VoiceThera. This allows you to save sessions, track your progress, and build a personal meditation library.',
  },
  {
    question: 'What is the Personal Biography feature?',
    answer: 'Your Personal Biography is an optional profile that helps us understand your background, preferences, and goals. When enabled, we use this information to create even more deeply personalized sessions that resonate with your unique life experiences.',
  },
  {
    question: 'Are sessions saved automatically?',
    answer: 'Yes! All generated sessions are automatically saved to your history. You can access them anytime from the "Sessions" section and mark favorites for quick access.',
  },
  {
    question: 'What are Group Sessions?',
    answer: 'Group Sessions allow you to create meditations for multiple people at once - perfect for families, teams, or classes. You can customize the session to include each participant\'s name and preferences.',
  },
  {
    question: 'What are Audio Gifts?',
    answer: 'Audio Gifts allow you to send personalized wellness sessions to friends and family. You can create a custom meditation tailored to someone special and share it as a thoughtful, meaningful gift.',
  },
  {
    question: 'How do credits work?',
    answer: 'Credits are used to generate sessions. Different subscription plans include different amounts of credits. You can view your credit balance and purchase more from the Credits & Billing section in your preferences.',
  },
  {
    question: 'How do I change my voice preference?',
    answer: 'Go to Preferences to choose your preferred voice and other audio settings. You can select from various calming voices to find the one that resonates most with you.',
  },
];

const supportOptions = [
  {
    icon: Mail,
    title: 'Email Support',
    description: 'Get help via email within 24 hours',
    action: 'support@voicethera.com',
    actionType: 'email',
  },
  {
    icon: Book,
    title: 'Help Center',
    description: 'Browse our comprehensive guides',
    action: 'View Guides',
    actionType: 'docs',
  },
];

export default function HelpPage() {
  const { user } = useAuth();
  const { hasPractitionerAccess } = useUserRole();

  const handleAction = (actionType: string, action: string) => {
    if (actionType === 'email') {
      window.location.href = `mailto:${action}`;
    } else if (actionType === 'docs') {
      window.location.href = '/help-center';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      {/* Navigation Header */}
      <div className="border-b bg-card/50 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <Link to={user ? '/app' : '/'}>
            <Button variant="outline" size="sm" className="rounded-full">
              <Home className="h-4 w-4 mr-2" />
              Home
            </Button>
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center">
            <FileQuestion className="h-16 w-16 mx-auto mb-6 text-primary" />
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            How Can We Help?
          </h1>
            <p className="text-xl text-muted-foreground">
              Find answers to common questions or get in touch with our support team.
            </p>
          </div>
        </div>
      </section>

      {/* Browse Complete Guides Banner */}
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-4">
          {/* User Guides Card */}
          <Card className="border-2 border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-primary/10">
                    <Book className="h-8 w-8 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-1">Browse Complete User Guides</h3>
                    <p className="text-sm text-muted-foreground">
                      Comprehensive documentation covering all VoiceThera features
                    </p>
                  </div>
                </div>
                <Link to="/help-center">
                  <Button size="lg" className="whitespace-nowrap">
                    View Help Center
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Demo Videos Card */}
          <Card className="border-2 border-blue-500/20 bg-gradient-to-r from-blue-500/5 to-blue-500/10">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-blue-500/10">
                    <Video className="h-8 w-8 text-blue-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-1">Watch Demo Videos</h3>
                    <p className="text-sm text-muted-foreground">
                      Learn how to use VoiceThera with our video tutorials
                    </p>
                  </div>
                </div>
                <Link to="/demo-videos">
                  <Button size="lg" variant="outline" className="whitespace-nowrap border-blue-500/50 hover:bg-blue-500/10">
                    Watch Videos
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Practitioner Resources Card - Only for practitioners */}
          {hasPractitionerAccess && (
            <Card className="border-2 border-purple-500/20 bg-gradient-to-r from-purple-500/5 to-purple-500/10">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-purple-500/10">
                      <Briefcase className="h-8 w-8 text-purple-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-1">Practitioner Resources</h3>
                      <p className="text-sm text-muted-foreground">
                        Guides for managing clients, organizations, and sessions
                      </p>
                    </div>
                  </div>
                  <Link to="/practitioner/resources">
                    <Button size="lg" variant="outline" className="whitespace-nowrap border-purple-500/50 hover:bg-purple-500/10">
                      View Resources
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Support Options */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">Get Support</h2>
          <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
            {supportOptions.map((option) => (
              <Card key={option.title} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                    <option.icon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-lg">{option.title}</CardTitle>
                  <CardDescription className="text-sm">{option.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => handleAction(option.actionType, option.action)}
                  >
                    {option.action}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-background rounded-lg px-6 border"
              >
                <AccordionTrigger className="text-left hover:no-underline">
                  <span className="font-semibold">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">Quick Links</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to="/help-center" className="block">
              <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer">
                <CardHeader>
                  <CardTitle className="text-lg">Getting Started</CardTitle>
                  <CardDescription>Learn the basics of VoiceThera</CardDescription>
                </CardHeader>
              </Card>
            </Link>
            <Link to="/demo-videos" className="block">
              <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer">
                <CardHeader>
                  <CardTitle className="text-lg">Demo Videos</CardTitle>
                  <CardDescription>Watch video tutorials</CardDescription>
                </CardHeader>
              </Card>
            </Link>
            <Link to="/preferences" className="block">
              <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer">
                <CardHeader>
                  <CardTitle className="text-lg">Account Settings</CardTitle>
                  <CardDescription>Manage your profile and preferences</CardDescription>
                </CardHeader>
              </Card>
            </Link>
            <Link to="/biographies" className="block">
              <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer">
                <CardHeader>
                  <CardTitle className="text-lg">Biography Library</CardTitle>
                  <CardDescription>Manage your personal biographies</CardDescription>
                </CardHeader>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold mb-4">Still Need Help?</h2>
          <p className="text-muted-foreground mb-6">
            Our support team is here to help you with any questions or issues.
          </p>
          <Button size="lg" onClick={() => window.location.href = 'mailto:support@voicethera.com'}>
            <Mail className="mr-2 h-4 w-4" />
            Contact Support
          </Button>
        </div>
      </section>
    </div>
  );
}
